import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Eye, User, Building, MoreHorizontal, Quote, Filter, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTestimonials, useDeleteTestimonial } from '@/hooks/use-testimonials';
import { useToast } from '@/hooks/use-toast';
import { DeleteAlertDialog } from '@/components/admin/DeleteAlertDialog';
import { Skeleton } from '@/components/ui/skeleton';

export default function AdminDepoimentos() {
  const [searchTerm, setSearchTerm] = useState('');
  const { data: testimonials = [], isLoading, error } = useTestimonials();
  const deleteTestimonial = useDeleteTestimonial();
  const { toast } = useToast();

  // Filtrar depoimentos baseado no termo de busca
  const filteredTestimonials = testimonials.filter(testimonial =>
    testimonial.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    testimonial.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
    testimonial.quote.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const confirmDelete = (id: string, name: string) => {
    setDeleteId(id);
    setDeleteTitle(name);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteTestimonial.mutateAsync(deleteId);
        toast({
          title: "Depoimento excluído",
          description: `O depoimento de "${deleteTitle}" foi excluído com sucesso.`,
        });
        setDeleteDialogOpen(false);
      } catch (error) {
        toast({
          title: "Erro ao excluir",
          description: "Ocorreu um erro ao excluir o depoimento.",
          variant: "destructive",
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <Skeleton className="h-8 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 text-center py-20">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <MessageSquare className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-red-900">Erro ao carregar depoimentos</h2>
        <p className="text-red-700">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Depoimentos</h1>
          <p className="text-muted-foreground hidden md:block">
            Gerencie o feedback e avaliações de seus clientes
          </p>
        </div>
        <Button asChild className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
          <Link to="/admin/depoimentos/novo">
            <Plus className="mr-2 h-4 w-4" />
            Novo Depoimento
          </Link>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-0 shadow-lg overflow-hidden backdrop-blur-sm bg-card/50">
          <CardHeader className="border-b bg-muted/20 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

              {/* Search Bar */}
              <div className="relative flex-1 max-w-md group">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-blue-500 transition-colors" />
                <Input
                  placeholder="Buscar por nome, empresa ou conteúdo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-muted group-focus-within:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span>{filteredTestimonials.length} resultados</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[300px] pl-6">Autor</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead className="max-w-[400px]">Depoimento</TableHead>
                  <TableHead className="text-right pr-6">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {filteredTestimonials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center h-40 text-muted-foreground">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mb-3">
                            <MessageSquare className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                          <p className="font-medium">Nenhum depoimento encontrado</p>
                          <p className="text-sm text-muted-foreground/70">
                            {searchTerm ? 'Tente buscar com outros termos.' : 'Adicione seu primeiro depoimento.'}
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTestimonials.map((testimonial, index) => (
                      <motion.tr
                        key={testimonial.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: index * 0.05 }}
                        className="group hover:bg-muted/40 transition-colors border-b border-border/50 last:border-0"
                        style={{ display: 'table-row' }}
                        layoutId={testimonial.id}
                      >
                        <TableCell className="pl-6 py-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-10 w-10 ring-2 ring-background shadow-sm">
                              <AvatarImage src={testimonial.avatar} alt={testimonial.name} className="object-cover" />
                              <AvatarFallback className="bg-muted text-muted-foreground">
                                <User className="h-5 w-5" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground group-hover:text-blue-600 transition-colors">{testimonial.name}</span>
                              <span className="text-xs text-muted-foreground">{testimonial.role}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal text-xs flex w-fit items-center gap-1.5 py-1 px-2">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            {testimonial.company}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="relative pl-3 border-l-2 border-muted-foreground/20 italic text-muted-foreground text-sm line-clamp-2 max-w-[400px]">
                            "{testimonial.quote}"
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-transparent">
                                  <span className="sr-only">Abrir menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[160px]">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/depoimentos/${testimonial.id}/editar`} className="cursor-pointer">
                                    <Edit className="mr-2 h-4 w-4" />
                                    Editar
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => confirmDelete(testimonial.id, testimonial.name)}
                                  className="text-red-500 focus:text-red-500 cursor-pointer"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      <DeleteAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Excluir depoimento de "${deleteTitle}"?`}
        description="Esta ação não pode ser desfeita. Isso excluirá permanentemente o depoimento."
        onConfirm={handleDelete}
        isDeleting={deleteTestimonial.isPending}
      />
    </div>
  );
}
